import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Translator } from "./translator";

const Community = () => {
  const [communities, setCommunities] = useState([]);
  const [communitiesView, setCommunitiesView] = useState([]);
  const [active, setActive] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { translateText, lang } = Translator();

/*  const ALLOWED_EMAILS = [
    "info@emozion-arti.com",
    "omarsamirmohammad@gmail.com",
    "osmohammad@emozion-arti.com",
  ];
  const savedUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch {
      return null;
    }
  })();
  const userEmail = (savedUser?.email || "").toLowerCase();
  const isPrivileged = ALLOWED_EMAILS.includes(userEmail);
*/
  const baseTextByLang = {
    it: {
      text1: 'Community "Italiani nel Mondo"',
      text2: 'Unisciti alla Community "Italiani nel Mondo"',
      text3: "Partecipa e contribuisci alla preservazione della tua Cultura Italiana!",
      text4: "Riscopri e Arricchisci Le Tue Radici Culturali",
      text5: "Clicca per Scoprire",
      text6: "Interessi:",
      text7: "🚀 Prossimamente",
      textAll: "Tutti",
      textErrore: "Errore:",
    },
    en: {
      text1: '"Italians in the World" Community',
      text2: 'Join the "Italians in the World" Community',
      text3: "Take part and help preserve your Italian cultural heritage!",
      text4: "Rediscover and Enrich Your Cultural Roots",
      text5: "Click to Discover",
      text6: "Interests:",
      text7: "🚀 Coming soon",
      textAll: "All",
      textErrore: "Error:",
    },
    fr: {
      text1: 'Community "Italiens dans le Monde"',
      text2: 'Rejoignez la Community "Italiens dans le Monde"',
      text3: "Participez et contribuez à préserver votre culture italienne !",
      text4: "Redécouvrez et enrichissez vos racines culturelles",
      text5: "Cliquez pour découvrir",
      text6: "Intérêts :",
      text7: "🚀 Prochainement",
      textAll: "Tous",
      textErrore: "Erreur:",
    },
  };


  const baseText = baseTextByLang[lang] || baseTextByLang.it;
  const [translatedText, setTranslatedText] = useState(baseText);

  // ---- Static it/en/fr + Translator fallback for others ----
  useEffect(() => {
    let cancelled = false;

    // 1) For Italian / English / French → use static copy, no API
    if (lang === "it" || lang === "en" || lang === "fr") {
      setTranslatedText(baseTextByLang[lang] || baseTextByLang.it);
      return;
    }

    // 2) Other languages → translate from Italian base using Translator
    (async () => {
      try {
        const source = baseTextByLang.it;
        const entries = Object.entries(source);

        const translatedEntries = await Promise.all(
          entries.map(async ([k, v]) => {
            if (!v) return [k, v];
            const out = await translateText(v);
            return [k, out || v]; // fallback to Italian string
          })
        );

        if (!cancelled) {
          setTranslatedText(Object.fromEntries(translatedEntries));
        }
      } catch (err) {
        if (!cancelled) {
          // On error, fall back to Italian
          setTranslatedText(baseTextByLang.it);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [lang, translateText]);
  
  
  // ---- Single fetch effect (deduped) with abort + timeout ----
  useEffect(() => {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort("timeout"), 15000);

    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch(
          "https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com/getAllCommunities",
          { signal: ctrl.signal, cache: "no-store" }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setCommunities(Array.isArray(data) ? data : []);
        setCommunitiesView(Array.isArray(data) ? data : []);
      } catch (err) {
        if (err.name !== "AbortError") {
          setError(err?.message || "Network error");
          setCommunities([]);
        }
      } finally {
        clearTimeout(timer);
        setLoading(false);
      }
    })();

    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, []);


  // ---- Translate backend fields (category, title, desc) for list cards ----
  useEffect(() => {
    if (!communities.length) return;

    if (lang === "it") {
      setCommunitiesView(communities);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const translated = await Promise.all(
          communities.map(async (c) => {
            const [newCategory, newTitle, newDesc] = await Promise.all([
              translateText(c.category || ""),
              translateText(c.title || ""),
              translateText(c.desc || ""),
            ]);

            return {
              ...c,
              category: newCategory || c.category,
              title: newTitle || c.title,
              desc: newDesc || c.desc,
            };
          })
        );

        if (!cancelled) {
          setCommunitiesView(translated);
        }
      } catch (e) {
        console.error("Community translation failed:", e);
        if (!cancelled) {
          // fallback: show original data
          setCommunitiesView(communities);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [communities, lang, translateText]);


  // ---- Categories from data (defensive) ----
  const categories = useMemo(() => {
    const set = new Set(
      communitiesView
        .map((c) => (typeof c?.category === "string" ? c.category : null))
        .filter(Boolean)
    );
    return [...set];
  }, [communitiesView]);


  const filtered = useMemo(
    () =>
      (communitiesView || []).filter(
        (c) => active === "ALL" || c?.category === active
      ),
    [communitiesView, active]
  );

  return (
    <main className="w-full bg-white text-neutral-900">
      {/* Hero */}
      <header
        className="bg-[#324B8E] w-full max-w-[1920px] mx-auto px-6 md:px-16
                   pt-[max(1rem,env(safe-area-inset-top))]
                   pb-[max(1rem,env(safe-area-inset-bottom))]
                   flex items-center min-h-[220px] md:min-h-[300px]"
      >
        <h1
          className="font-[Montserrat] font-bold text-white
                     text-[1.75rem] sm:text-[2.5rem] md:text-[3rem] lg:text-[3.5rem] xl:text-[4rem] leading-[1.1]
                     max-w-[92vw] sm:max-w-[90%] lg:max-w-[1100px] xl:max-w-[1200px]"
        >
          {translatedText.text1 || baseText.text1}
        </h1>
      </header>

      {/* Intro */}
      <section className="w-full mx-auto px-6 md:px-8">
        <div className="max-w-screen-xl mx-auto text-center py-8 md:py-12">
          <div className="w-full overflow-x-auto ios-scroll scrollbar-none">
            <h2
              className="font-[Montserrat] font-bold text-black
                        text-[1.25rem] sm:text-[1.75rem] md:text-[2rem] lg:text-[2.25rem] xl:text-[2.5rem]
                        leading-snug text-center
                        max-w-[95vw] sm:max-w-[85%] lg:max-w-[1100px] xl:max-w-[1200px]
                        mx-auto whitespace-normal break-words text-pretty"
            >
              {translatedText.text2 || baseText.text2}
            </h2>
          </div>

          <p className="mt-4 text-neutral-800/90 text-[clamp(1rem,2.2vw,1.125rem)] leading-relaxed max-w-3xl mx-auto px-1">
            {translatedText.text3 || baseText.text3}
          </p>

          <div
            className="w-[90%] sm:w-[75%] md:w-[60%] lg:w-[50%] h-[4px] bg-[#324B8E] mt-4 mx-auto rounded-full"
            aria-hidden="true"
          />

          <div className="mt-8">
            <h3
              className="font-[Montserrat] font-semibold text-black
                         text-[clamp(1.125rem,2.4vw,1.5rem)] leading-snug
                         max-w-3xl mx-auto"
            >
              {translatedText.text4 || baseText.text4}
            </h3>
          </div>
        </div>
      </section>

      {/* Category chips */}
      <section className="w-full">
        <div className="max-w-screen-xl mx-auto px-4 md:px-6">
          <nav
            className="mt-2 mb-4 flex items-center justify-center"
            aria-label={translatedText.text6 || baseText.text6}
          >
            <div className="flex gap-2 flex-nowrap overflow-x-auto ios-scroll scrollbar-none snap-x snap-mandatory px-1 py-2 md:flex-wrap md:overflow-visible">
              <button
                onClick={() => setActive("ALL")}
                className={
                  "snap-start px-3 py-2 min-h-[40px] rounded-full text-sm border transition " +
                  (active === "ALL"
                    ? "bg-[#324B8E] text-white border-[#324B8E] focus-visible:ring-2 focus-visible:ring-[#324B8E]"
                    : "text-[#324B8E] border-[#324B8E] hover:bg-[#324B8E]/10 focus-visible:ring-2 focus-visible:ring-[#324B8E]/60")
                }
                aria-pressed={active === "ALL"}
              >
                {translatedText.textAll || baseText.textAll}
              </button>

              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActive(active === cat ? "ALL" : cat)}
                  className={
                    "snap-start px-3 py-2 min-h-[40px] rounded-full text-sm border transition " +
                    (active === cat
                      ? "bg-[#324B8E] text-white border-[#324B8E] focus-visible:ring-2 focus-visible:ring-[#324B8E]"
                      : "text-[#324B8E] border-[#324B8E] hover:bg-[#324B8E]/10 focus-visible:ring-2 focus-visible:ring-[#324B8E]/60")
                  }
                  aria-pressed={active === cat}
                >
                  {cat}
                </button>
              ))}
            </div>
          </nav>
        </div>
      </section>

      {/* Cards */}
      <section className="w-full">
        <div className="max-w-screen-xl mx-auto px-4 md:px-6 pb-24">
          {loading && (
            <div
              className="grid gap-4 sm:gap-5 lg:gap-6
                          grid-cols-[repeat(auto-fit,minmax(190px,1fr))]
                          sm:grid-cols-[repeat(auto-fit,minmax(240px,1fr))]
                          md:grid-cols-[repeat(auto-fit,minmax(300px,1fr))]
                          lg:grid-cols-[repeat(auto-fit,minmax(340px,1fr))]
                          xl:grid-cols-[repeat(auto-fit,minmax(360px,1fr))]"
            >
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="aspect-square rounded-xl bg-neutral-200 animate-pulse"
                />
              ))}
            </div>
          )}

          {!loading && error && (
            <p className="text-center text-red-600 mt-6">{translatedText.textErrore || baseText.textErrore} {error}</p>
          )}

          {!loading && !error && filtered.length === 0 && (
            <p className="text-center text-neutral-600 mt-6">
              {translatedText.text7 || baseText.text7}
            </p>
          )}

          <div
            className="grid gap-4 sm:gap-5 lg:gap-6
                        grid-cols-[repeat(auto-fit,minmax(190px,1fr))]
                        sm:grid-cols-[repeat(auto-fit,minmax(240px,1fr))]
                        md:grid-cols-[repeat(auto-fit,minmax(300px,1fr))]
                        lg:grid-cols-[repeat(auto-fit,minmax(340px,1fr))]
                        xl:grid-cols-[repeat(auto-fit,minmax(360px,1fr))]"
          >
            {!loading &&
              !error &&
              filtered.map((item) => {
                const imgSrc =
                  item?.image ||
                  "/images/placeholders/community-fallback.webp"; // ensure you have one

                const CardInner = (
                  <article
                    className="relative aspect-square bg-white rounded-xl overflow-hidden shadow border border-gray-200
                              transition-transform duration-500 ease-out
                              motion-safe:hover:scale-[1.04] motion-safe:md:hover:scale-[1.06] hover:-translate-y-1.5 hover:shadow-2xl hover:z-30
                              focus-within:ring-2 focus-within:ring-[#324B8E] group"
                  >
                    <img
                      src={imgSrc}
                      alt={item?.title || "Community"}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-110"
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent pointer-events-none" />

                    <div className="absolute bottom-0 inset-x-0 text-white p-3 z-10">
                      <h4 className="text-sm md:text-base font-semibold line-clamp-1">
                        {item?.title || "—"}
                      </h4>

                      <div className="mt-1 flex items-center gap-2 text-xs md:text-sm opacity-90">
                        <span
                          title="Mi piace totali"
                          className="inline-flex items-center gap-1"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                            aria-hidden="true"
                          >
                            <path d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 18.656l-6.828-6.828a4 4 0 010-5.656z" />
                          </svg>
                          {item?.likes ?? 0}
                        </span>

                        <span
                          title="Commenti totali"
                          className="inline-flex items-center gap-1"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                            aria-hidden="true"
                          >
                            <path d="M18 13a3 3 0 01-3 3H8l-4 3v-3H3a3 3 0 01-3-3V5a3 3 0 013-3h12a3 3 0 013 3v8z" />
                          </svg>
                          {item?.comments ?? 0}
                        </span>

                        <span
                          title="Follower"
                          className="inline-flex items-center gap-1"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            strokeWidth="1.8"
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M17 20v-2a4 4 0 00-4-4H7a4 4 0 00-4 4v2M9 12a4 4 0 100-8 4 4 0 000 8zM23 20v-2a4 4 0 00-3-3.874"
                            />
                            <circle cx="20" cy="8" r="3" />
                          </svg>
                          {item?.followers ?? 0}
                        </span>
                      </div>
                    </div>

                    <div
                      className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 pointer-events-none
                                 transition-all duration-500 opacity-100 translate-y-0
                                 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:translate-y-1
                                 group-hover:opacity-100 group-hover:translate-y-0"
                    >
                      <button
                        className="pointer-events-auto px-3 py-1.5 rounded-full text-xs md:text-sm
                                   bg-[#324B8E] text-white font-medium shadow-md
                                   hover:bg-[#24366b] focus-visible:ring-2 focus-visible:ring-[#324B8E]
                                   transition-colors duration-300"
                        aria-label={translatedText.text5 || baseText.text5}
                      >
                        {translatedText.text5 || baseText.text5}
                      </button>
                    </div>

{/*                    {!isPrivileged && (
                      <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px] flex items-center justify-center z-30">
                        <div className="bg-white text-black px-3 py-1 rounded-full text-xs font-semibold shadow">
                          {translatedText.text7 || baseText.text7}
                        </div>
                      </div>
                    )}
*/}                  </article>
                );

                //return isPrivileged ? (
                return (
                  <Link
                    to={`/community/${item?.id}`}
                    key={item?.id}
                    className="block outline-none focus-visible:ring-2 focus-visible:ring-[#324B8E] rounded-xl"
                    aria-label={`${item?.title || "Community"} — apri`}
                  >
                    {CardInner}
                  </Link>
/*                ) : (
                  <div
                    key={item?.id}
                    className="cursor-not-allowed select-none"
                    aria-disabled="true"
                    role="button"
                    tabIndex={-1}
                  >
                    {CardInner}
                  </div>
*/                );
              })}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Community;
